import type { ChatState, ChatAction, ConversationData, ChatMessage } from './chatTypes';
import { initialChatState } from './chatTypes';

function sortByTime(convos: ConversationData[]): void {
  convos.sort((a, b) => {
    const timeA = a.lastMessageTime?.getTime() || a.createdAt.getTime();
    const timeB = b.lastMessageTime?.getTime() || b.createdAt.getTime();
    return timeB - timeA;
  });
}

/**
 * Deduplicate DM conversations that share the same peerAddress but have
 * different IDs (e.g. after installation revocation or race between
 * placeholder and SDK-resolved conversation). Groups are passed through.
 */
function deduplicateByPeerAddress(conversations: ConversationData[]): ConversationData[] {
  const seen = new Map<string, number>(); // peerAddress -> index in result
  const result: ConversationData[] = [];

  for (const convo of conversations) {
    if (convo.isGroup || !convo.peerAddress) {
      result.push(convo);
      continue;
    }

    const key = convo.peerAddress.toLowerCase();
    const existingIdx = seen.get(key);

    if (existingIdx === undefined) {
      seen.set(key, result.length);
      result.push(convo);
    } else {
      // Keep whichever has the most recent message; merge metadata from both
      const existing = result[existingIdx];
      const existingTime = existing.lastMessageTime?.getTime() || 0;
      const incomingTime = convo.lastMessageTime?.getTime() || 0;
      const primary = incomingTime > existingTime ? convo : existing;
      const secondary = incomingTime > existingTime ? existing : convo;

      result[existingIdx] = {
        ...secondary,
        ...primary,
        peerAddress: primary.peerAddress || secondary.peerAddress,
        lastMessageText: primary.lastMessageText ?? secondary.lastMessageText,
        lastMessageTime: primary.lastMessageTime ?? secondary.lastMessageTime,
        lastMessageSender: primary.lastMessageSender ?? secondary.lastMessageSender,
      };
    }
  }
  return result;
}

function mergeConversation(existing: ConversationData, incoming: ConversationData): ConversationData {
  return {
    ...existing,
    ...incoming,
    peerAddress: incoming.peerAddress || existing.peerAddress,
    groupName: incoming.groupName ?? existing.groupName,
    members: incoming.members ?? existing.members,
    lastMessageText: incoming.lastMessageText ?? existing.lastMessageText,
    lastMessageTime: incoming.lastMessageTime ?? existing.lastMessageTime,
    lastMessageSender: incoming.lastMessageSender ?? existing.lastMessageSender,
  };
}

export function chatReducer(state: ChatState, action: ChatAction): ChatState {
  switch (action.type) {
    case 'SET_CLIENT':
      return { ...state, client: action.payload };

    case 'SET_INITIALIZING':
      return { ...state, isInitializing: action.payload };

    case 'SET_INITIALIZED':
      return { ...state, isInitialized: action.payload, initProgress: null };

    case 'SET_INIT_ERROR':
      return { ...state, initError: action.payload, isInitializing: false, initProgress: null };

    case 'SET_INIT_PROGRESS':
      return { ...state, initProgress: action.payload };

    case 'SET_HAS_CLOUD_BACKUP':
      return { ...state, hasCloudBackup: action.payload };

    case 'SET_CONVERSATIONS': {
      const deduped = deduplicateByPeerAddress(action.payload);
      return { ...state, conversations: deduped };
    }

    case 'ADD_CONVERSATION': {
      const existingIdx = state.conversations.findIndex((c) => c.id === action.payload.id);

      // Secondary dedup: for DMs, check if a conversation with the same
      // peer address already exists under a different conversation ID.
      const peerDupIdx =
        existingIdx < 0 && !action.payload.isGroup && action.payload.peerAddress
          ? state.conversations.findIndex(
              (c) =>
                !c.isGroup &&
                c.peerAddress &&
                c.peerAddress.toLowerCase() === action.payload.peerAddress.toLowerCase()
            )
          : -1;

      // If already present (by ID), merge fresh metadata instead of dropping the update.
      // This is important when we add a placeholder from stream first, then
      // reconcile later with authoritative SDK conversation data.
      if (existingIdx >= 0) {
        const merged = mergeConversation(state.conversations[existingIdx], action.payload);
        const updated = state.conversations.map((c, i) => (i === existingIdx ? merged : c));
        sortByTime(updated);
        return { ...state, conversations: updated };
      }

      // If a DM with the same peer address exists under a different ID,
      // merge into the existing entry instead of creating a duplicate.
      if (peerDupIdx >= 0) {
        const oldId = state.conversations[peerDupIdx].id;
        const merged = mergeConversation(state.conversations[peerDupIdx], action.payload);
        const updated = state.conversations.map((c, i) => (i === peerDupIdx ? merged : c));
        sortByTime(updated);
        const newState: ChatState = { ...state, conversations: updated };
        // Preserve active selection when conversation ID changes during dedup
        if (state.activeConversationId === oldId && merged.id !== oldId) {
          newState.activeConversationId = merged.id;
        }
        return newState;
      }

      const added = [action.payload, ...state.conversations];
      sortByTime(added);
      return { ...state, conversations: added };
    }

    case 'UPDATE_CONVERSATION_LAST_MESSAGE': {
      const { id, text, time, sender } = action.payload;
      const updated = state.conversations.map((c) =>
        c.id === id
          ? { ...c, lastMessageText: text, lastMessageTime: time, lastMessageSender: sender }
          : c
      );
      // Re-sort by last message time (most recent first)
      updated.sort((a, b) => {
        const timeA = a.lastMessageTime?.getTime() || a.createdAt.getTime();
        const timeB = b.lastMessageTime?.getTime() || b.createdAt.getTime();
        return timeB - timeA;
      });
      return { ...state, conversations: updated };
    }

    case 'UPDATE_CONVERSATION_MEMBERS': {
      const { id, members } = action.payload;
      return {
        ...state,
        conversations: state.conversations.map((c) =>
          c.id === id ? { ...c, members } : c
        ),
      };
    }

    case 'SET_ACTIVE_CONVERSATION':
      return { ...state, activeConversationId: action.payload };

    case 'SET_MESSAGES':
      return {
        ...state,
        messages: {
          ...state.messages,
          [action.payload.conversationId]: action.payload.messages,
        },
      };

    case 'APPEND_MESSAGE': {
      const { conversationId, message } = action.payload;
      const existing = state.messages[conversationId] || [];
      // Avoid duplicate messages
      if (existing.some((m) => m.id === message.id)) return state;
      return {
        ...state,
        messages: {
          ...state.messages,
          [conversationId]: [...existing, message],
        },
      };
    }

    case 'PREPEND_MESSAGES': {
      const { conversationId, messages } = action.payload;
      const existing = state.messages[conversationId] || [];
      const existingIds = new Set(existing.map((m) => m.id));
      const newMessages = messages.filter((m) => !existingIds.has(m.id));
      return {
        ...state,
        messages: {
          ...state.messages,
          [conversationId]: [...newMessages, ...existing],
        },
      };
    }

    case 'MERGE_MESSAGES': {
      const { conversationId, messages: incoming } = action.payload;
      const existing = state.messages[conversationId] || [];
      const map = new Map<string, ChatMessage>();
      for (const m of existing) map.set(m.id, m);
      for (const m of incoming) map.set(m.id, m);
      const merged = Array.from(map.values()).sort(
        (a, b) => a.sentAt.getTime() - b.sentAt.getTime()
      );
      return { ...state, messages: { ...state.messages, [conversationId]: merged } };
    }

    case 'SET_LOADING_MESSAGES':
      return {
        ...state,
        loadingMessages: {
          ...state.loadingMessages,
          [action.payload.conversationId]: action.payload.loading,
        },
      };

    case 'SET_HAS_MORE_MESSAGES':
      return {
        ...state,
        hasMoreMessages: {
          ...state.hasMoreMessages,
          [action.payload.conversationId]: action.payload.hasMore,
        },
      };

    case 'INCREMENT_UNREAD': {
      const convId = action.payload;
      const newCount = (state.unreadCounts[convId] || 0) + 1;
      return {
        ...state,
        unreadCounts: { ...state.unreadCounts, [convId]: newCount },
        totalUnread: state.totalUnread + 1,
      };
    }

    case 'SET_UNREAD_COUNT': {
      const { conversationId, count } = action.payload;
      const oldCount = state.unreadCounts[conversationId] || 0;
      return {
        ...state,
        unreadCounts: { ...state.unreadCounts, [conversationId]: count },
        totalUnread: Math.max(0, state.totalUnread - oldCount + count),
      };
    }

    case 'CLEAR_UNREAD': {
      const convId = action.payload;
      const cleared = state.unreadCounts[convId] || 0;
      return {
        ...state,
        unreadCounts: { ...state.unreadCounts, [convId]: 0 },
        totalUnread: Math.max(0, state.totalUnread - cleared),
      };
    }

    case 'SET_SEARCH_QUERY':
      return { ...state, searchQuery: action.payload };

    case 'SET_SYNCING':
      return { ...state, isSyncing: action.payload };

    case 'SET_CONVERSATION_FILTER':
      return { ...state, conversationFilter: action.payload };

    case 'SET_INSTALLATION_LIMIT':
      return {
        ...state,
        showInstallationManager: true,
        pendingClient: action.payload.client,
        pendingInboxId: action.payload.inboxId,
        isInitializing: false,
        initProgress: null,
      };

    case 'CLEAR_INSTALLATION_LIMIT':
      return { ...state, showInstallationManager: false, pendingClient: null, pendingInboxId: null };

    case 'SET_PEER_DISPLAY_NAMES': {
      // payload: Record<lowercased address, "@username">
      const names = action.payload;
      return {
        ...state,
        conversations: state.conversations.map((c) => {
          if (c.isGroup || !c.peerAddress) return c;
          const display = names[c.peerAddress.toLowerCase()];
          return display ? { ...c, peerDisplayName: display } : c;
        }),
      };
    }

    case 'MARK_REMOVED_FROM_GROUP': {
      return {
        ...state,
        conversations: state.conversations.map((c) =>
          c.id === action.payload ? { ...c, isRemovedFromGroup: true } : c
        ),
      };
    }

    case 'CLEAR_REMOVED_FROM_GROUP': {
      return {
        ...state,
        conversations: state.conversations.map((c) =>
          c.id === action.payload ? { ...c, isRemovedFromGroup: false } : c
        ),
      };
    }

    case 'RESET':
      return initialChatState;

    default:
      return state;
  }
}
